import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  EventEntity,
  EventStatus,
  EventStatusLabel,
  useFindManyEventsQuery,
  useLazyFindOneEventQuery,
} from 'src/store/eventApi';
import { Tabs } from 'src/ui/Tabs';
import { Badge } from 'src/ui/Badge';
import { Button } from 'src/ui/Button';
import { Icon } from 'src/ui/Icon';
import { EmptyState } from 'src/ui/EmptyState';
import Outline from './outline';
import Editor from './editor';
import Attendees from './attendees';
import Signup from './signup';
import MatchResults from './matchResults';

export const NEW_EVENT_ID = 'new';

const STATUS_BADGE_COLOR: Record<EventStatus, string> = {
  [EventStatus.DRAFT]: 'gray',
  [EventStatus.PUBLISHED]: 'green',
  [EventStatus.CANCELLED]: 'red',
  [EventStatus.COMPLETED]: 'blue',
};

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

const TABS = [
  { id: 'edit', label: 'Edit Event', icon: 'edit' as const },
  { id: 'attendees', label: 'Attendees', icon: 'users' as const },
  { id: 'signup', label: 'Sign-up', icon: 'qr-code' as const },
  { id: 'match', label: 'Match Result', icon: 'target' as const },
];

const Events = () => {
  const { data: eventsData } = useFindManyEventsQuery();
  const { eventId } = useParams<{ eventId?: string }>();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<EventEntity | null>(null);
  const [activeTab, setActiveTab] = useState('edit');
  const [fetchOneEvent] = useLazyFindOneEventQuery();

  useEffect(() => {
    if (!eventId || eventId === NEW_EVENT_ID) {
      setSelectedEvent(null);
      return;
    }
    fetchOneEvent(eventId)
      .unwrap()
      .then((result) => {
        setSelectedEvent(result);
      })
      .catch((error) => {
        console.error('Failed to fetch event:', error);
        setSelectedEvent(null);
        navigate('/events');
      });
  }, [eventId, fetchOneEvent, navigate]);

  // Reset to edit tab when switching events
  useEffect(() => {
    setActiveTab('edit');
  }, [eventId]);

  const handleSelect = (id: string) => navigate(`/events/${id}`);
  const handleNew = () => navigate(`/events/${NEW_EVENT_ID}`);
  const handleClose = () => navigate('/events');

  const isNew = eventId === NEW_EVENT_ID;
  const events = eventsData?.data ?? [];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: eventId ? '340px 1fr' : '1fr',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Left — event list */}
      <div
        style={{
          borderRight: eventId ? '1px solid var(--border)' : 'none',
          overflow: 'hidden',
          maxWidth: eventId ? 'none' : 720,
          marginLeft: eventId ? 0 : 'auto',
          marginRight: eventId ? 0 : 'auto',
          width: '100%',
        }}
      >
        <Outline
          events={events}
          selectedEventId={eventId ?? null}
          onSelect={handleSelect}
          onNew={handleNew}
        />
      </div>

      {/* Right — detail panel */}
      {eventId && (
        <div className="col" style={{ overflow: 'hidden' }}>
          {/* Event header */}
          <div
            style={{
              padding: '14px 22px 0',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
              background: 'var(--bg-canvas)',
            }}
          >
            <div
              className="row gap-3"
              style={{ marginBottom: 12, alignItems: 'flex-start' }}
            >
              <div className="grow">
                <div className="row gap-2">
                  <h1
                    className="page-title"
                    style={{
                      fontSize: 'var(--fs-xl)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '420px',
                    }}
                  >
                    {isNew ? 'New Event' : selectedEvent?.title || '(untitled)'}
                  </h1>
                  {!isNew && selectedEvent && (
                    <Badge
                      color={STATUS_BADGE_COLOR[selectedEvent.status]}
                      size="sm"
                      dot
                    >
                      {EventStatusLabel[selectedEvent.status]}
                    </Badge>
                  )}
                </div>
                {!isNew && selectedEvent && (
                  <div
                    className="row gap-3 text-muted"
                    style={{ fontSize: 'var(--fs-sm)', marginTop: 3 }}
                  >
                    <span className="row gap-1">
                      <Icon name="calendar" size={14} />
                      {fmtDate(selectedEvent.startAt)}
                    </span>
                    {selectedEvent.location && (
                      <span className="row gap-1">
                        <Icon name="map-pin" size={14} />
                        {selectedEvent.location}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                title="Close"
                onClick={handleClose}
              >
                <Icon name="x" size={16} />
              </Button>
            </div>

            <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />
          </div>

          {/* Tab content */}
          <div className="grow" style={{ overflow: 'hidden' }}>
            {activeTab === 'edit' && (
              <Editor
                eventId={eventId}
                onCreated={(id) => navigate(`/events/${id}`)}
                onDeleted={() => navigate('/events')}
              />
            )}
            {activeTab === 'attendees' && <Attendees eventId={eventId} />}
            {activeTab === 'signup' && <Signup eventId={eventId} />}
            {activeTab === 'match' && <MatchResults eventId={eventId} />}
          </div>
        </div>
      )}

      {!eventId && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <EmptyState icon="calendar" title="Select an event" />
        </div>
      )}
    </div>
  );
};

export default Events;
